// EditContactSidebar.jsx — same revised style (phosphor-react + clean drawer + consistent buttons)
import React, { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { X, FloppyDisk, User, EnvelopeSimple, Phone, ChatCircleText } from "phosphor-react"
import { TextInput, TextArea } from "../../components"

const EditContactSidebar = ({ isOpen, onClose, contact, onContactUpdated }) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  })

  useEffect(() => {
    if (!isOpen) return
    setFormData({
      firstName: contact?.firstName || "",
      lastName: contact?.lastName || "",
      email: contact?.email || "",
      phone: contact?.phone || "",
      message: contact?.message || "",
    })
  }, [isOpen, contact])

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "unset"
    return () => (document.body.style.overflow = "unset")
  }, [isOpen])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!contact?.id) return toast.error("Invalid contact")

    try {
      setLoading(true)
      const res = await axiosInstance.put(`/contacts/update/${contact.id}`, formData)
      toast.success(res?.data?.message || "Contact updated successfully")
      onContactUpdated?.(res?.data)
      onClose?.()
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating contact")
      console.error("Error updating contact:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50">
          {/* overlay */}
          <div className="absolute inset-0 bg-black/70" onClick={onClose} />

          {/* drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="absolute inset-y-0 right-0 w-full sm:w-[420px] bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 overflow-y-auto"
          >
            {/* header */}
            <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start justify-between gap-3">
                <div className="text-start">
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Edit Contact</h2>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Update contact info and save changes.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  icon={User}
                />
                <TextInput
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  icon={User}
                />
              </div>

              <TextInput
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="name@email.com"
                icon={EnvelopeSimple}
              />

              <TextInput
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+212 ..."
                icon={Phone}
              />

              <TextArea
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Message..."
                icon={ChatCircleText}
              />

              {/* actions */}
              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin inline-block size-4 border-[2px] border-current border-t-transparent rounded-full" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FloppyDisk size={18} />
                      Update Contact
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default EditContactSidebar
